import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { loadReference } from "../../lib/reference";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import { REF_TYPE, usePlatformCan } from "../../lib/platform";

const RefRow = ({ r, canEdit, onSaved }) => {
  const [label, setLabel] = useState(r.label);
  const save = async (patch) => {
    try { await api.patch(`/admin/settings/reference/${r.id}`, patch); toast.success("Modification enregistrée"); onSaved(); loadReference(true); }
    catch (e) { toast.error(formatApiError(e)); setLabel(r.label); }
  };
  return (
    <tr data-testid={`ref-row-${r.type}-${r.code}`}>
      <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
      <td className="px-4 py-2">{canEdit ? <Input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => label.trim() !== r.label && save({ label })} data-testid={`ref-label-${r.type}-${r.code}`} /> : r.label}</td>
      {r.type === "currency" && <td className="px-4 py-2 text-xs">{r.symbol}</td>}
      <td className="px-4 py-2 text-right">
        <Switch checked={r.active} disabled={!canEdit || (r.protected && r.active)} onCheckedChange={(v) => save({ active: v })} data-testid={`ref-active-${r.type}-${r.code}`} />
        {r.protected && <p className="text-[10px] text-slate-400">requis</p>}
      </td>
    </tr>
  );
};

const AddRef = ({ type, onSaved }) => {
  const [f, setF] = useState({ code: "", label: "", symbol: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/settings/reference", { type, ...f }); toast.success("Élément ajouté"); setF({ code: "", label: "", symbol: "" }); onSaved(); loadReference(true); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={add} className="mb-4 flex flex-col gap-2 sm:flex-row" data-testid={`ref-add-form-${type}`}>
      <Input className="sm:w-40" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="Code (ex : sn, xof)" data-testid={`ref-add-code-${type}`} />
      <Input className="flex-1" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Libellé" data-testid={`ref-add-label-${type}`} />
      {type === "currency" && <Input className="sm:w-28" value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value })} placeholder="Symbole" data-testid="ref-add-symbol-currency" />}
      <Button type="submit" data-testid={`ref-add-submit-${type}`}><Plus className="mr-1 h-4 w-4" />Ajouter</Button>
    </form>
  );
};

const RefTab = ({ type }) => {
  const can = usePlatformCan();
  const { data, loading, error, reload } = useApi(`/admin/settings/reference?type=${type}`);
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div>
      {can("settings:manage") && <AddRef type={type} onSaved={reload} />}
      <div className="card-surface overflow-x-auto"><table className="w-full min-w-[480px] text-sm">
        <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Libellé</th>{type === "currency" && <th className="px-4 py-2">Symbole</th>}<th className="px-4 py-2 text-right">Actif</th></tr></thead>
        <tbody className="divide-y">{data.map((r) => <RefRow key={r.id} r={r} canEdit={can("settings:manage")} onSaved={reload} />)}</tbody>
      </table></div>
      <p className="mt-2 text-xs text-slate-500">Les éléments ne sont jamais supprimés : désactivez-les pour préserver l'historique des données existantes.</p>
    </div>
  );
};

const RefSelect = ({ type, value, onChange, id }) => {
  const { data } = useApi(`/admin/settings/reference?type=${type}`);
  return <select className="field-select mt-1.5" value={value} onChange={onChange} data-testid={id}>{data?.filter((r) => r.active).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}</select>;
};

const General = () => {
  const can = usePlatformCan();
  const { data, loading, error, reload } = useApi("/admin/settings/general");
  const [f, setF] = useState(null);
  useEffect(() => { if (data) { setF(data); } }, [data]);
  if (loading || !f) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try { const { data: d } = await api.put("/admin/settings/general", { platform_name: f.platform_name, support_email: f.support_email, default_country: f.default_country, default_language: f.default_language, default_currency: f.default_currency, company_signup_open: f.company_signup_open }); setF(d); toast.success("Paramètres enregistrés"); loadReference(true); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className="card-surface grid max-w-2xl gap-4 p-6 sm:grid-cols-2" data-testid="general-settings-form">
      <div><Label>Nom de la plateforme</Label><Input className="mt-1.5" value={f.platform_name} onChange={set("platform_name")} data-testid="general-platform-name-input" /></div>
      <div><Label>Email de support</Label><Input className="mt-1.5" value={f.support_email} onChange={set("support_email")} placeholder="support@…" data-testid="general-support-email-input" /></div>
      <div><Label>Pays par défaut</Label><RefSelect type="country" value={f.default_country} onChange={set("default_country")} id="general-country-select" /></div>
      <div><Label>Langue par défaut</Label><RefSelect type="language" value={f.default_language} onChange={set("default_language")} id="general-language-select" /></div>
      <div><Label>Devise par défaut</Label><RefSelect type="currency" value={f.default_currency} onChange={set("default_currency")} id="general-currency-select" /></div>
      <label className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm sm:col-span-2">
        <span><b>Inscriptions d'entreprises ouvertes</b><br /><span className="text-xs text-slate-500">Fermées : les nouvelles demandes sont refusées par le serveur.</span></span>
        <Switch checked={f.company_signup_open} onCheckedChange={(v) => setF({ ...f, company_signup_open: v })} data-testid="general-signup-open-switch" />
      </label>
      {f.updated_by && <p className="text-xs text-slate-500 sm:col-span-2">Dernière modification par {f.updated_by}.</p>}
      {can("settings:manage") && <div className="sm:col-span-2"><Button type="submit" data-testid="general-settings-save">Enregistrer</Button></div>}
    </form>
  );
};

export default function AdminSettings() {
  return (
    <div>
      <PageHeader title="Secteurs & paramètres" description="Listes de référence et paramètres généraux de la plateforme. Chaque modification est auditée." />
      <Tabs defaultValue="sector">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start">
          {Object.entries(REF_TYPE).map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`settings-tab-${k}`}>{l}</TabsTrigger>)}
          <TabsTrigger value="general" data-testid="settings-tab-general">Paramètres généraux</TabsTrigger>
        </TabsList>
        {Object.keys(REF_TYPE).map((k) => <TabsContent key={k} value={k}><RefTab type={k} /></TabsContent>)}
        <TabsContent value="general"><General /></TabsContent>
      </Tabs>
    </div>
  );
}
